import { environmentOf, init } from '@appercut/round-vue';
import { readHyperswitchIntent } from '../shared/utils/hyperswitchRail';
import { PaymentContractError } from '../utils/paymentContractError';

/**
 * The SDK cannot mount without a client secret, so on this rail the intent is created when the
 * checkout opens rather than on the pay click. The backend gives it a two-hour window.
 *
 * The gateway is asserted here because mounting the Hyperswitch SDK against a Stripe intent fails
 * later and further from the cause.
 */
export function useHyperswitchCheckout() {
  const gatewayStore = useKegelGatewayStore();
  const { $logger } = useNuxtApp();

  let warming: Promise<string | null> | null = null;

  function warmHyperswitchSdk(): Promise<string | null> {
    const configKey = gatewayStore.lagoHyperswitchPk;
    if (!configKey) return Promise.resolve(null);

    warming ??= init({ publishableKey: configKey }).then(() => configKey, () => null);

    return warming;
  }

  function reportEnvironmentMismatch(warmedKey: string, intentKey: string): void {
    if (environmentOf(warmedKey) === environmentOf(intentKey)) return;

    $logger.error(new Error(
      `Hyperswitch key environment mismatch: config key is ${environmentOf(warmedKey)}, `
      + `intent key is ${environmentOf(intentKey)}`,
    ), { tags: { flow: 'payment', step: 'init_sdk' } });
  }

  async function initHyperswitchSdk(
    ensureIntent: () => Promise<Record<string, any> | null>,
    isCurrent: () => boolean = () => true,
  ): Promise<void> {
    const [read, warmedKey] = await Promise.all([
      ensureIntent().then(readHyperswitchIntent),
      warmHyperswitchSdk(),
    ]);

    if (read.kind !== 'intent') {
      const detail = read.kind === 'wrong-gateway' ? `gateway: ${read.gateway || 'none'}` : read.kind;
      throw new PaymentContractError(`Hyperswitch intent cannot be mounted (${detail})`);
    }

    if (!isCurrent()) return;

    if (warmedKey !== read.publishableKey) {
      if (warmedKey) reportEnvironmentMismatch(warmedKey, read.publishableKey);
      await init({ publishableKey: read.publishableKey });
    }
  }

  return { warmHyperswitchSdk, initHyperswitchSdk };
}
